import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  ApiV2InfiniteQueryOptions,
  apiV2Request,
  getDefaultInfiniteQueryOptionsV2,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";
import {
  OpeningOrderFeeListParametersType,
  OpeningOrderFeeListResponseType,
  OpeningOrderFeeListWithCountResponseType,
} from "@sellar-io/api-v2/types";

export const useOpeningOrderFeeList = <
  TData = OpeningOrderFeeListResponseType,
>(
  params: OpeningOrderFeeListParametersType,
  options?: ApiV2InfiniteQueryOptions<TData>,
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useInfiniteQuery({
    queryKey: [
      user?.uid,
      business?.id,
      ServerStateKeys.OpeningOrderFee,
      params.search,
      params.sortBy,
      params.sortDir,
    ],
    queryFn: ({ pageParam }) =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: "opening-order-fee",
        params: {
          ...params,
          offset: pageParam,
        },
      }).then((data) => apiV2AssertResponse<TData>(data).data),
    ...getDefaultInfiniteQueryOptionsV2({
      context: { scope: "business", user, business: business },
      options: { ...options, limit: params.limit },
    }),
  });
};

export const useOpeningOrderFeePaginatedList = <
  TData = OpeningOrderFeeListWithCountResponseType,
>(
  params: OpeningOrderFeeListParametersType,
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useQuery({
    queryKey: [
      user?.uid,
      business?.id,
      ServerStateKeys.OpeningOrderFee,
      ...Object.values(params),
    ],
    queryFn: () =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: "opening-order-fee/with-count",
        params,
      }).then((data) => apiV2AssertResponse<TData>(data).data),
    placeholderData: (prev) => prev,
  });
};
